import { useEffect, useState } from 'react';
import { VAT_TREATMENTS } from '@arcnet/contracts';
import type { Invoice } from '@arcnet/contracts';
import { useTenants } from '../../lib/api/hooks.js';
import { useAllInvoices, useCreditNote, useRecordPayment } from '../../lib/api/hooks-invoices.js';
import { ErrorBanner, Segmented } from '../../components/ui/Primitives.js';
import { ApiClientError } from '../../lib/api/client.js';
import { useStepUp, StepUpPrompt } from '../../lib/auth/stepup.js';
import { formatMoney } from '../../lib/format.js';
import { docDate } from './InvoiceDocument.js';

/**
 * What staff do to issued invoices (docs/28 B2): credit one, or record a payment
 * against one or several. Both are final and both ask for a recent password
 * check. The action itself carries what happens next, so a replay after the
 * password check finishes the job.
 */

type Treatment = (typeof VAT_TREATMENTS)[number];
const VAT_LABEL: Record<Treatment, string> = { standard: 'Standard', zero_rated: 'Zero-rated', exempt: 'Exempt' };

const shown = (e: unknown): ApiClientError | null =>
  e instanceof ApiClientError && e.code !== 'auth.step_up_required' ? e : null;

/** Today in South Africa, as YYYY-MM-DD. */
const todayIso = (): string => new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Johannesburg' });

/** Rand as whole cents; null for anything that is not an amount. */
const toCents = (v: string): number | null =>
  /^\d{1,12}(\.\d{1,2})?$/.test(v.trim()) ? Math.round(Number(v.trim()) * 100) : null;

export function CreditNoteForm({ invoice, onDone, onCancel }: {
  invoice: Invoice;
  onDone: () => void;
  onCancel: () => void;
}) {
  const credit = useCreditNote(invoice.id);
  const step = useStepUp();
  const canCreditAll = Number(invoice.amount_credited) === 0;
  const [whole, setWhole] = useState<'all' | 'amount'>(canCreditAll ? 'all' : 'amount');
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [treatment, setTreatment] = useState<Treatment>('standard');

  const submit = () => void step.run(() => credit.mutateAsync(whole === 'all'
    ? { reason: reason.trim() }
    : {
        reason: reason.trim(),
        lines: [{
          description: description.trim() || reason.trim(), quantity: '1',
          unit_price: amount.trim(), vat_treatment: treatment,
        }],
      }).then(onDone)).catch(() => undefined);

  const err = shown(credit.error);

  return (
    <div className="arc-card" style={{ marginBottom: 14 }}>
      <div className="arc-card-head"><h2 className="arc-h2">Credit note for {invoice.number}</h2></div>
      <form className="arc-card-body" onSubmit={(e) => { e.preventDefault(); submit(); }}>
        <StepUpPrompt step={step} what="Issuing a credit note" />
        {err && <ErrorBanner error={err} />}

        <div className="arc-field">
          <span className="arc-label">Credit</span>
          <Segmented label="Credit" value={whole} onChange={setWhole}
            options={[
              ...(canCreditAll ? [['all', `The whole invoice, ${formatMoney(invoice.total, invoice.currency)}`] as const] : []),
              ['amount', 'An amount'] as const,
            ]} />
        </div>

        {whole === 'amount' && (
          <div className="arc-grid arc-grid-2" style={{ marginBottom: 14 }}>
            <div className="arc-field" style={{ margin: 0 }}>
              <label className="arc-label" htmlFor="credit-description">Description</label>
              <input id="credit-description" className="arc-input" value={description}
                placeholder="What is being credited" onChange={(e) => setDescription(e.target.value)} />
            </div>
            <div className="arc-row" style={{ gap: 12, alignItems: 'flex-start' }}>
              <div className="arc-field" style={{ margin: 0, flex: 1 }}>
                <label className="arc-label" htmlFor="credit-amount">Amount excl. VAT</label>
                <input id="credit-amount" className="arc-input arc-num" inputMode="decimal" required
                  value={amount} onChange={(e) => setAmount(e.target.value)} />
              </div>
              <div className="arc-field" style={{ margin: 0, flex: 1 }}>
                <label className="arc-label" htmlFor="credit-vat">VAT</label>
                <select id="credit-vat" className="arc-select" value={treatment}
                  onChange={(e) => setTreatment(e.target.value as Treatment)}>
                  {VAT_TREATMENTS.map((t) => <option key={t} value={t}>{VAT_LABEL[t]}</option>)}
                </select>
              </div>
            </div>
          </div>
        )}

        <div className="arc-field">
          <label className="arc-label" htmlFor="credit-reason">Reason</label>
          <input id="credit-reason" className="arc-input" required minLength={3} value={reason}
            placeholder="Printed on the credit note" onChange={(e) => setReason(e.target.value)} />
        </div>

        <div className="arc-row" style={{ gap: 8 }}>
          <button type="submit" className="arc-btn arc-btn-primary" disabled={credit.isPending}>
            {credit.isPending ? 'Issuing…' : 'Issue credit note'}
          </button>
          <button type="button" className="arc-btn arc-btn-ghost" onClick={onCancel}>Cancel</button>
        </div>
        <p className="arc-hint" style={{ marginTop: 10 }}>
          A credit note is numbered, final and emailed with its PDF. It reduces what is due; it does not
          refund what was paid.
        </p>
      </form>
    </div>
  );
}

/**
 * A payment that arrived outside PayFast, split across an account's unpaid
 * invoices. Opened from an invoice, that invoice starts filled in with what is due.
 */
export function RecordPaymentForm({ tenantId: given, focusInvoiceId, onDone, onCancel }: {
  /** The account, when the form is opened from one of its invoices. */
  tenantId: string | null;
  focusInvoiceId?: string;
  onDone: (receiptNumber: string) => void;
  onCancel: () => void;
}) {
  const tenants = useTenants({ limit: 200 });
  const [tenantId, setTenantId] = useState(given ?? '');
  const unpaid = useAllInvoices('unpaid', tenantId || undefined, !!tenantId);
  const record = useRecordPayment();
  const step = useStepUp();
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  const [method, setMethod] = useState<'eft' | 'cash' | 'other'>('eft');
  const [reference, setReference] = useState('');
  const [receivedOn, setReceivedOn] = useState(todayIso());

  useEffect(() => {
    const inv = focusInvoiceId ? unpaid.data?.data.find((x) => x.id === focusInvoiceId) : undefined;
    if (inv) setAmounts((a) => (inv.id in a ? a : { ...a, [inv.id]: inv.amount_due }));
  }, [unpaid.data, focusInvoiceId]);

  const invoices = unpaid.data?.data ?? [];
  const chosen = invoices.filter((x) => (amounts[x.id] ?? '') !== '');
  const totalCents = chosen.reduce((sum, x) => sum + (toCents(amounts[x.id]!) ?? 0), 0);
  const invalid = chosen.some((x) => {
    const c = toCents(amounts[x.id]!);
    return c === null || c <= 0 || c > (toCents(x.amount_due) ?? 0);
  });
  const currency = invoices[0]?.currency ?? 'ZAR';

  const toggle = (id: string, due: string) => setAmounts((a) => {
    const next = { ...a };
    if (id in next) delete next[id]; else next[id] = due;
    return next;
  });

  const submit = () => void step.run(() => record.mutateAsync({
    tenant_id: tenantId,
    method,
    reference: reference.trim() || null,
    received_on: receivedOn,
    allocations: chosen.map((x) => ({ invoice_id: x.id, amount: amounts[x.id]!.trim() })),
  }).then((r) => onDone(r.receipt_number))).catch(() => undefined);

  const err = shown(record.error);

  return (
    <div className="arc-card" style={{ marginBottom: 14 }}>
      <div className="arc-card-head"><h2 className="arc-h2">Record a payment</h2></div>
      <form className="arc-card-body" onSubmit={(e) => { e.preventDefault(); submit(); }}>
        <StepUpPrompt step={step} what="Recording a payment" />
        {err && <ErrorBanner error={err} />}

        {!given && (
          <div className="arc-field">
            <label className="arc-label" htmlFor="payment-account">Account</label>
            <select id="payment-account" className="arc-select" value={tenantId} required
              onChange={(e) => { setTenantId(e.target.value); setAmounts({}); }}>
              <option value="" disabled>Choose an account</option>
              {tenants.data?.data.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        )}

        {tenantId && (unpaid.isLoading
          ? <div className="arc-skeleton" style={{ height: 80, marginBottom: 14 }} />
          : invoices.length === 0
            ? <p className="arc-muted">This account has no invoices waiting for payment.</p>
            : (
              <div className="arc-table-wrap" style={{ marginBottom: 14 }}>
                <table className="arc-table">
                  <thead>
                    <tr>
                      <th style={{ width: 36 }} />
                      <th>Invoice</th><th>Due</th>
                      <th className="arc-td-right">Due now</th>
                      <th style={{ width: 150 }} className="arc-td-right">This payment</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((x) => (
                      <tr key={x.id}>
                        <td>
                          <input type="checkbox" checked={x.id in amounts} aria-label={`Pay ${x.number}`}
                            onChange={() => toggle(x.id, x.amount_due)} />
                        </td>
                        <td className="arc-mono" style={{ fontWeight: 600 }}>{x.number}</td>
                        <td className="arc-num arc-subtle">{docDate(x.due_date)}</td>
                        <td className="arc-td-right arc-num">{formatMoney(x.amount_due, x.currency)}</td>
                        <td>
                          <input className="arc-input arc-num" inputMode="decimal"
                            aria-label={`Amount against ${x.number}`} value={amounts[x.id] ?? ''}
                            onChange={(e) => setAmounts((a) => ({ ...a, [x.id]: e.target.value }))} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}

        <div className="arc-field">
          <span className="arc-label">Paid by</span>
          <Segmented label="Paid by" value={method} onChange={setMethod}
            options={[['eft', 'EFT'], ['cash', 'Cash'], ['other', 'Other']] as const} />
        </div>

        <div className="arc-grid arc-grid-2">
          <div className="arc-field" style={{ margin: 0 }}>
            <label className="arc-label" htmlFor="payment-received">Received on</label>
            <input id="payment-received" className="arc-input" type="date" required
              value={receivedOn} onChange={(e) => setReceivedOn(e.target.value)} />
          </div>
          <div className="arc-field" style={{ margin: 0 }}>
            <label className="arc-label" htmlFor="payment-reference">Their reference</label>
            <input id="payment-reference" className="arc-input" value={reference}
              placeholder="As it shows on the bank statement" onChange={(e) => setReference(e.target.value)} />
          </div>
        </div>

        <div className="arc-row" style={{ gap: 12, marginTop: 14, flexWrap: 'wrap' }}>
          <button type="submit" className="arc-btn arc-btn-primary"
            disabled={record.isPending || chosen.length === 0 || invalid}>
            {record.isPending ? 'Recording…' : `Record ${formatMoney((totalCents / 100).toFixed(2), currency)}`}
          </button>
          <button type="button" className="arc-btn arc-btn-ghost" onClick={onCancel}>Cancel</button>
          {invalid && <span className="arc-muted">No more against an invoice than is due on it.</span>}
        </div>
        <p className="arc-hint" style={{ marginTop: 10 }}>
          Numbered as one receipt and emailed to the account&apos;s billing addresses.
        </p>
      </form>
    </div>
  );
}
